import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  lotesAPI,
  type LoteParams,
  type CrearLoteData,
  type ActualizarLoteData,
} from '../services/lotesAPI';
import type {
  LotesResponse,
  EstadisticasLotes,
  LoteIngredienteBackend,
} from '../types/lotes';

// Re-export types for consumers of this hook
export type { LotesResponse, EstadisticasLotes, LoteIngredienteBackend };

// Query keys para React Query
export const lotesKeys = {
  all: ['lotes'] as const,
  lists: () => [...lotesKeys.all, 'list'] as const,
  list: (params?: LoteParams) => [...lotesKeys.lists(), params] as const,
  details: () => [...lotesKeys.all, 'detail'] as const,
  detail: (id: string) => [...lotesKeys.details(), id] as const,
  ingrediente: (ingredienteId: number) =>
    [...lotesKeys.all, 'ingrediente', ingredienteId] as const,
  estadisticas: (ingredienteId?: number) =>
    [...lotesKeys.all, 'estadisticas', ingredienteId] as const,
};

// Hook para listar lotes
export const useLotes = (params?: LoteParams) => {
  return useQuery({
    queryKey: lotesKeys.list(params),
    queryFn: () => lotesAPI.listar(params),
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
};

// Hook para obtener un lote específico
export const useLote = (id: string, enabled = true) => {
  return useQuery({
    queryKey: lotesKeys.detail(id),
    queryFn: () => lotesAPI.obtener(id),
    enabled: enabled && !!id,
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
};

// Hook para obtener lotes por ingrediente
export const useLotesPorIngrediente = (
  ingredienteId: number,
  enabled = true
) => {
  return useQuery({
    queryKey: lotesKeys.ingrediente(ingredienteId),
    queryFn: () => lotesAPI.obtenerPorIngrediente(ingredienteId),
    enabled: enabled && !!ingredienteId,
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
};

// Hook para obtener estadísticas de lotes
export const useEstadisticasLotes = (ingredienteId?: number) => {
  return useQuery({
    queryKey: lotesKeys.estadisticas(ingredienteId),
    queryFn: () => lotesAPI.obtenerEstadisticas(ingredienteId),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

// Hook para crear lote
export const useCrearLote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CrearLoteData) => lotesAPI.crear(data),
    onSuccess: () => {
      // Invalidar todas las queries de lotes
      queryClient.invalidateQueries({ queryKey: lotesKeys.all });
    },
  });
};

// Hook para actualizar la fecha de vencimiento de un lote
export const useActualizarVencimientoLote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      fechaVencimiento,
    }: {
      id: number;
      fechaVencimiento: string | null;
    }) => lotesAPI.actualizarVencimiento(id, fechaVencimiento),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lotesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: lotesKeys.estadisticas() });
    },
  });
};

// Hook para actualizar lote
export const useActualizarLote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ActualizarLoteData }) =>
      lotesAPI.actualizar(id, data),
    onSuccess: (_, { id }) => {
      // Invalidar queries específicas
      queryClient.invalidateQueries({ queryKey: lotesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lotesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: lotesKeys.estadisticas() });
    },
  });
};
